import { z } from 'zod';
import { isValidCampaignSlug } from './campaigns.js';
// Plain values with no zod behind them (trader identity, app links, product limits). Also
// published on their own as `@chefty/shared-types/constants`, for bundles that need only these.
export * from './constants.js';
/**
 * Single source of truth for the API contract.
 *
 * Every request/response shape is a Zod schema here, exported alongside its
 * inferred type. The backend validates with these (via fastify-type-provider-zod)
 * and the client/admin/mobile apps import the inferred types — so client and
 * server can never drift.
 */
// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------
export const userSchema = z.object({
    id: z.string().uuid(),
    // Nullable: email is mutable profile data provisioned from the Keycloak `email`
    // claim, which a token may omit. Identity is keyed on keycloakId, not email.
    email: z.string().email().nullable(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * Account-deletion grace window. `DELETE /users/me` soft-deletes (sign out + mark
 * `deletedAt`); the account is recoverable by signing in again within this many
 * days. After it elapses, the purge job hard-deletes all data, S3 objects, and
 * (when configured) the Keycloak identity. 30 days matches Google Play's allowance.
 */
export const ACCOUNT_DELETION_GRACE_DAYS = 30;
/**
 * Returned by `DELETE /users/me` (200): the account is now soft-deleted and will
 * be permanently purged at `purgeAt` unless the user signs in again before then.
 */
export const accountDeletionScheduledSchema = z.object({
    status: z.literal('deletion_scheduled'),
    purgeAt: z.coerce.date(),
});
export const userProfileSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    avatar: z.string().nullish(),
    bio: z.string().nullish(),
});
export const createUserSchema = z.object({
    email: z.string().email(),
    firstName: z.string().min(1).optional(),
    lastName: z.string().min(1).optional(),
});
// ---------------------------------------------------------------------------
// Auth (Keycloak-derived claims)
// ---------------------------------------------------------------------------
export const authUserSchema = z.object({
    // The INTERNAL user id (`users.id`) — resolved/provisioned from the Keycloak
    // identity on first authenticated request (ticket 1.1). All domain FKs
    // (recipes.user_id, import_jobs.user_id) reference this, never the Keycloak sub.
    id: z.string().uuid(),
    // The Keycloak `sub` — the external identity, kept distinct from the internal id.
    keycloakId: z.string(),
    email: z.string().email(),
    roles: z.array(z.string()),
    // True when the identity is an API-issued anonymous (device) user — a users row
    // with a synthetic `anon:<uuid>` keycloakId — rather than a Keycloak account.
    anonymous: z.boolean(),
});
/** Response of POST /auth/anonymous — the device's durable anonymous credential. */
export const anonymousAuthResponseSchema = z.object({
    token: z.string(),
    user: z.object({ id: z.string().uuid() }),
});
export const linkAnonymousRequestSchema = z.object({
    anonToken: z.string().min(1),
});
/** Result of merging an anonymous user's data into the authenticated account. */
export const linkAnonymousResponseSchema = z.object({
    merged: z.boolean(),
    recipesMoved: z.number().int().nonnegative(),
    importJobsMoved: z.number().int().nonnegative(),
});
/**
 * Native Sign in with Apple bootstrap. The app sends the Apple-issued identity
 * token (verified server-side against Apple's JWKS), plus name/email which Apple
 * only returns on the FIRST authorization. `anonToken` carries the device's prior
 * anonymous identity so it can be merged, mirroring /auth/link-anonymous.
 */
export const appleSignInRequestSchema = z.object({
    identityToken: z.string().min(1),
    fullName: z.string().min(1).optional(),
    email: z.string().email().optional(),
    anonToken: z.string().min(1).optional(),
});
/** Keycloak token set returned by the bridge; mirrors the OIDC token response. */
export const appleSignInResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    /** Access-token lifetime in seconds (as Keycloak returns `expires_in`). */
    expiresIn: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Generic API envelopes & pagination
// ---------------------------------------------------------------------------
export const apiErrorSchema = z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
});
/** Standard error body for non-2xx responses. */
export const errorResponseSchema = z.object({ message: z.string() });
export const paginationQuerySchema = z.object({
    page: z.coerce.number().min(1).default(1),
    pageSize: z.coerce.number().min(1).max(100).default(20),
    sortBy: z.string().default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
// ---------------------------------------------------------------------------
// Recipes
// ---------------------------------------------------------------------------
export const recipeSourceTypeSchema = z.enum([
    'text',
    'url',
    'image',
    'screenshot',
    'youtube',
    'tiktok',
    'instagram',
    'manual',
]);
export const ingredientSchema = z.object({
    name: z.string(),
    quantity: z.string().nullish(),
    unit: z.string().nullish(),
    note: z.string().nullish(),
});
export const recipeStepSchema = z.object({
    order: z.number().int().nonnegative(),
    text: z.string(),
    ingredientRefs: z.array(z.string()).default([]),
});
/**
 * ISO 639-1 language code (lowercase, e.g. 'en', 'ru'), optionally with a
 * region subtag ('pt-br'). Kept open (no enum) — supported languages are a
 * product decision that shouldn't require a schema migration.
 */
export const languageCodeSchema = z
    .string()
    .regex(/^[a-z]{2}(-[a-z]{2})?$/, 'must be an ISO 639-1 code like "en" or "pt-br"');
export const recipeSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    title: z.string(),
    description: z.string().nullish(),
    /** Language the recipe content is written in; null when never detected/set. */
    language: languageCodeSchema.nullish(),
    sourceType: recipeSourceTypeSchema,
    sourceUrl: z.string().url().nullish(),
    imageUrl: z.string().url().nullish(),
    servings: z.number().int().positive().nullish(),
    prepTimeMinutes: z.number().int().nonnegative().nullish(),
    cookTimeMinutes: z.number().int().nonnegative().nullish(),
    ingredients: z.array(ingredientSchema),
    steps: z.array(recipeStepSchema),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const createRecipeSchema = recipeSchema
    .omit({ id: true, userId: true, createdAt: true, updatedAt: true })
    .extend({
    ingredients: z.array(ingredientSchema).default([]),
    steps: z.array(recipeStepSchema).default([]),
});
export const updateRecipeSchema = createRecipeSchema.partial();
/** Columns a caller may sort the recipe list by. Whitelisted to keep a
 * client-supplied `sortBy` from ever reaching SQL as a raw column name. */
export const recipeSortBySchema = z.enum(['createdAt', 'title']);
/** Querystring for `GET /api/recipes`: pagination + optional title search.
 * Reuses the shared pagination contract and narrows `sortBy` to the whitelist. */
export const recipeQuerySchema = paginationQuerySchema.extend({
    sortBy: recipeSortBySchema.default('createdAt'),
    // Trimmed; an empty/whitespace `q` is allowed and means "no filter" (the
    // service treats the empty string as falsy), so clearing a search box (`q=`)
    // returns all recipes rather than 400-ing.
    q: z.string().trim().optional(),
});
/** Paginated envelope returned by `GET /api/recipes`. Mobile/web consume the
 * inferred type so the list shape never drifts from the server. */
export const recipeListResponseSchema = z.object({
    items: z.array(recipeSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// AI import jobs
// ---------------------------------------------------------------------------
/** The only allowed import-job statuses. Do not invent new ones. */
export const aiJobStatusSchema = z.enum([
    'pending',
    'processing',
    'needs_review',
    'completed',
    'failed',
    'cancelled',
]);
/** Hosts accepted for a `youtube` source. */
const YOUTUBE_HOST_RE = /(^|\.)(youtube\.com|youtu\.be)$/i;
/** Hosts accepted for a `tiktok` source (incl. vm./vt. share short-links). */
const TIKTOK_HOST_RE = /(^|\.)tiktok\.com$/i;
/** Hosts accepted for an `instagram` source. */
const INSTAGRAM_HOST_RE = /(^|\.)(instagram\.com|instagr\.am)$/i;
function hostMatches(value, re) {
    try {
        return re.test(new URL(value).hostname);
    }
    catch {
        return false;
    }
}
function isYoutubeUrl(value) {
    return hostMatches(value, YOUTUBE_HOST_RE);
}
export function isTiktokUrl(value) {
    return hostMatches(value, TIKTOK_HOST_RE);
}
export function isInstagramUrl(value) {
    return hostMatches(value, INSTAGRAM_HOST_RE);
}
/**
 * Route a pasted link to its import source type. Single source of truth for
 * "which platform is this URL" so the mobile Add screen and the API's refine
 * rules can never disagree. Falls back to the generic `url` extractor.
 */
export function detectLinkSourceType(url) {
    if (isYoutubeUrl(url))
        return 'youtube';
    if (isTiktokUrl(url))
        return 'tiktok';
    if (isInstagramUrl(url))
        return 'instagram';
    return 'url';
}
/**
 * Host rules for the platform-specific link sources. One table drives both the
 * refine cases below and keeps the next platform from pasting another
 * structurally identical switch case that can drift from `detectLinkSourceType`.
 */
const PLATFORM_URL_RULES = {
    youtube: { test: isYoutubeUrl, expects: 'a youtube.com or youtu.be link' },
    tiktok: { test: isTiktokUrl, expects: 'a tiktok.com link' },
    instagram: { test: isInstagramUrl, expects: 'an instagram.com link' },
};
/**
 * Enforces that the provided input actually matches the declared `sourceType`,
 * so a mismatched job (e.g. `url` source with no `url`) is rejected at the
 * boundary instead of failing late in the worker. Shared by the request body
 * and the queue payload so they can never drift.
 */
function refineImportInput(data, ctx) {
    switch (data.sourceType) {
        case 'text':
            if (!data.text || data.text.trim().length === 0) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['text'],
                    message: "text is required when sourceType is 'text'",
                });
            }
            break;
        case 'url':
            if (!data.url) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['url'],
                    message: "url is required when sourceType is 'url'",
                });
            }
            break;
        case 'youtube':
        case 'tiktok':
        case 'instagram': {
            const rule = PLATFORM_URL_RULES[data.sourceType];
            if (!data.url) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['url'],
                    message: `url is required when sourceType is '${data.sourceType}'`,
                });
            }
            else if (!rule.test(data.url)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['url'],
                    message: `url must be ${rule.expects}`,
                });
            }
            break;
        }
        case 'image':
        case 'screenshot':
            if (!data.imageKey) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['imageKey'],
                    message: `imageKey is required when sourceType is '${data.sourceType}'`,
                });
            }
            break;
        case 'manual':
            ctx.addIssue({
                code: 'custom',
                path: ['sourceType'],
                message: 'manual recipes are created via POST /recipes, not imports',
            });
            break;
    }
}
/** Request body to start an import. Exactly one input is provided per source type. */
export const createImportJobSchema = z
    .object({
    sourceType: recipeSourceTypeSchema,
    text: z.string().optional(),
    url: z.string().url().optional(),
    imageKey: z.string().optional(),
    /** Skip the "already saved this link" check and import anyway (user override). */
    force: z.boolean().optional(),
})
    .superRefine(refineImportInput);
export const importJobSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    status: aiJobStatusSchema,
    sourceType: recipeSourceTypeSchema,
    /** AI-extracted draft (lands in needs_review for the user to confirm/edit). */
    draft: z.record(z.string(), z.unknown()).nullish(),
    error: z.string().nullish(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * Returned by POST /imports (200) when the incoming link is already saved in the
 * caller's cookbook. No job is enqueued; the client offers "Open" or "Add anyway"
 * (which retries with force: true). `status: 'duplicate'` is the discriminant
 * against ImportJob's AiJobStatus, which never takes that value.
 */
export const duplicateWarningSchema = z.object({
    status: z.literal('duplicate'),
    existingRecipe: z.object({ id: z.string().uuid(), title: z.string() }),
});
/** Plan tiers. `free` is the default; `plus` is the paid subscription. */
export const PLAN_TIERS = ['free', 'plus'];
export const planTierSchema = z.enum(PLAN_TIERS);
/**
 * Returned by POST /imports (402) when a free user has used their weekly import
 * allowance. No job is enqueued; the client surfaces an upgrade prompt.
 * `status: 'quota_exceeded'` is the discriminant against ImportJob's AiJobStatus
 * (which never takes that value) and against DuplicateWarning's 'duplicate'.
 */
export const quotaExceededSchema = z.object({
    status: z.literal('quota_exceeded'),
    /** The allowance that was hit (LIMITS.FREE_WEEKLY_IMPORTS). */
    limit: z.number().int(),
    /** The rolling window the allowance applies to (LIMITS.IMPORT_QUOTA_WINDOW_DAYS). */
    windowDays: z.number().int(),
});
/**
 * Returned by GET /subscriptions/me — a read-only snapshot of the caller's plan
 * and current import allowance, so the client can render the quota counter and
 * plan badge without attempting an import. `importsUsed` is the count of imports
 * started in the rolling window; `isPro` mirrors the server-side entitlement gate.
 */
export const entitlementSummarySchema = z.object({
    tier: planTierSchema,
    isPro: z.boolean(),
    weeklyImportLimit: z.number().int(),
    importsUsed: z.number().int(),
    windowDays: z.number().int(),
    /** End of the current paid period (ISO), or null when free / open-ended. */
    periodEnd: z.string().datetime().nullable(),
});
/**
 * Body for confirming an import in `needs_review` → a saved recipe. This is the
 * user's FINAL, possibly-edited recipe, so it is exactly `createRecipeSchema`:
 * the review gate means the recipe is built from what the user confirms, never
 * blindly from the raw AI draft. Aliased (not re-derived) so the two can't drift.
 */
export const confirmImportSchema = createRecipeSchema;
// ---------------------------------------------------------------------------
// Source-URL canonicalization (shared by api + worker for the extraction cache)
// ---------------------------------------------------------------------------
/** Query params that identify a marketing/share session, not the content. */
const TRACKING_PARAM_PREFIXES = ['utm_', 'mc_'];
const TRACKING_PARAMS = new Set(['fbclid', 'gclid', 'igshid', 'igsh', 'si', 'feature']);
function isTrackingParam(name) {
    const lower = name.toLowerCase();
    return TRACKING_PARAMS.has(lower) || TRACKING_PARAM_PREFIXES.some((p) => lower.startsWith(p));
}
/** Pull the video id out of any YouTube URL shape, or null when there is none. */
function youtubeVideoId(u) {
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    if (host === 'youtu.be')
        return u.pathname.split('/')[1] || null;
    if (!YOUTUBE_HOST_RE.test(host))
        return null;
    if (u.pathname === '/watch')
        return u.searchParams.get('v');
    const pathMatch = u.pathname.match(/^\/(?:shorts|embed|live|v)\/([^/?#]+)/);
    return pathMatch ? pathMatch[1] : null;
}
/**
 * Pull the numeric video id out of a full TikTok URL
 * (`tiktok.com/@user/video/{id}`, `/embed/v2/{id}`, `/v/{id}`), or null.
 * Share short-links (vm./vt.tiktok.com/XXXX) carry no id — they need a network
 * redirect to resolve, so they normalize generically and stay their own key
 * until the worker resolves them.
 */
function tiktokVideoId(u) {
    if (!TIKTOK_HOST_RE.test(u.hostname))
        return null;
    const m = u.pathname.match(/\/(?:video|embed\/v2|v)\/(\d+)/);
    return m ? m[1] : null;
}
/**
 * Pull the media shortcode out of an Instagram post/reel URL, or null.
 * `/p/`, `/reel/`, `/reels/`, `/tv/` (with or without a leading `/{username}`)
 * all address the same media by shortcode, so they fold to one key.
 */
function instagramShortcode(u) {
    if (!INSTAGRAM_HOST_RE.test(u.hostname))
        return null;
    const m = u.pathname.match(/\/(?:p|reels?|tv)\/([A-Za-z0-9_-]+)/);
    return m ? m[1] : null;
}
/**
 * Canonicalizes a recipe source URL into the extraction-cache key, so the same
 * content shared through different links (http vs https, `www.`, tracking
 * params, trailing slash, YouTube's many URL shapes) dedupes to ONE cache row.
 *
 * This is a cache key, not a display value — the original URL the user pasted
 * is preserved separately. Unparseable input is returned trimmed, never thrown:
 * a weird URL just becomes its own cache key.
 */
export function normalizeRecipeSourceUrl(url) {
    const trimmed = url.trim();
    let u;
    try {
        u = new URL(trimmed);
    }
    catch {
        return trimmed;
    }
    // Every YouTube shape (youtu.be, /shorts/, /embed/, m./music. hosts) collapses
    // to the one canonical watch URL.
    const videoId = youtubeVideoId(u);
    if (videoId)
        return `https://www.youtube.com/watch?v=${videoId}`;
    // TikTok/Instagram media ids collapse the username and path-shape variants to
    // one key per video. These are cache keys, not resolvable display URLs.
    const tiktokId = tiktokVideoId(u);
    if (tiktokId)
        return `https://www.tiktok.com/video/${tiktokId}`;
    const igShortcode = instagramShortcode(u);
    if (igShortcode)
        return `https://www.instagram.com/reel/${igShortcode}`;
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    // http and https serve the same recipe; fold the scheme so they share a row.
    const port = u.port && u.port !== '80' && u.port !== '443' ? `:${u.port}` : '';
    const path = u.pathname.replace(/\/+$/, '');
    const params = [...u.searchParams.entries()].filter(([name]) => !isTrackingParam(name));
    // Sort for stability: ?a=1&b=2 and ?b=2&a=1 are the same page.
    params.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    const query = params.length
        ? `?${params.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}`
        : '';
    return `https://${host}${port}${path}${query}`;
}
/** Source types whose extraction is cacheable by link (keyed on the URL). */
export const URL_CACHEABLE_SOURCE_TYPES = [
    'url',
    'youtube',
    'tiktok',
    'instagram',
];
export function isUrlCacheableSourceType(sourceType) {
    return URL_CACHEABLE_SOURCE_TYPES.includes(sourceType);
}
/** Payload enqueued onto BullMQ for the worker. Keep provider-agnostic. */
export const importJobQueuePayloadSchema = z
    .object({
    jobId: z.string().uuid(),
    userId: z.string().uuid(),
    sourceType: recipeSourceTypeSchema,
    text: z.string().optional(),
    url: z.string().url().optional(),
    imageKey: z.string().optional(),
})
    .superRefine(refineImportInput);
export const IMPORT_QUEUE_NAME = 'recipe-import';
// ---------------------------------------------------------------------------
// Recipe translations (token-deduplicated, content-addressed)
// ---------------------------------------------------------------------------
/**
 * The translatable slice of a recipe — exactly the fields an AI translation
 * rewrites. Numbers/provenance (servings, times, urls) are language-neutral
 * and never part of a translation.
 */
export const translatableRecipeContentSchema = z.object({
    title: z.string(),
    description: z.string().nullish(),
    ingredients: z.array(ingredientSchema),
    steps: z.array(recipeStepSchema),
});
/**
 * Deterministic serialization of translatable content — the preimage of the
 * translation-cache key. Translations are CONTENT-addressed, not recipe-
 * addressed: hash(content)+language identifies a translation, so identical
 * recipes (e.g. two users who confirmed the same extraction untouched) share
 * one stored translation and the AI is paid once, while an edited copy gets
 * its own hash and can never be served a stale translation. Field order is
 * pinned here; the array order of ingredients/steps is meaningful and kept.
 * (Pure string — hashing happens server-side; this package must stay free of
 * node:crypto for the mobile bundle.)
 */
export function canonicalTranslatableContent(content) {
    return JSON.stringify({
        title: content.title,
        description: content.description ?? null,
        ingredients: content.ingredients.map((i) => ({
            name: i.name,
            quantity: i.quantity ?? null,
            unit: i.unit ?? null,
            note: i.note ?? null,
        })),
        steps: content.steps.map((s) => ({
            order: s.order,
            text: s.text,
            ingredientRefs: s.ingredientRefs,
        })),
    });
}
/**
 * Lifecycle of a stored translation — the relevant subset of the shared AI job
 * statuses (there is no review gate for translations: they never overwrite the
 * user's recipe, they sit alongside it).
 */
export const recipeTranslationStatusSchema = z.enum([
    'pending',
    'processing',
    'completed',
    'failed',
]);
/** Request body for POST /recipes/:id/translate. */
export const translateRecipeRequestSchema = z.object({
    language: languageCodeSchema,
});
/**
 * A stored translation as returned by the API. `translated` is null until
 * `status` is 'completed'. `sourceLanguage` is what the translator detected
 * the original content to be written in.
 */
export const recipeTranslationSchema = z.object({
    id: z.string().uuid(),
    language: languageCodeSchema,
    sourceLanguage: languageCodeSchema.nullish(),
    status: recipeTranslationStatusSchema,
    translated: translatableRecipeContentSchema.nullish(),
    error: z.string().nullish(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
/**
 * Queue payload for the translation worker. Carries the content itself (not a
 * recipe id) — the cache row is content-addressed and must not depend on any
 * particular user's recipe row surviving until the job runs.
 */
export const translateJobQueuePayloadSchema = z.object({
    translationId: z.string().uuid(),
    targetLanguage: languageCodeSchema,
    content: translatableRecipeContentSchema,
});
export const RECIPE_TRANSLATE_QUEUE_NAME = 'recipe-translate';
// ---------------------------------------------------------------------------
// Files — image/screenshot upload (presigned)
// ---------------------------------------------------------------------------
/**
 * Image content types accepted for an import upload. The whitelist is the
 * contract: the upload-URL endpoint rejects anything outside it, and the worker
 * (4.5) can rely on the stored object being one of these.
 */
export const IMAGE_IMPORT_CONTENT_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
];
export const imageImportContentTypeSchema = z.enum(IMAGE_IMPORT_CONTENT_TYPES);
/** Canonical file extension per accepted content type (used to build the key). */
export const IMAGE_IMPORT_EXT = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/heic': 'heic',
};
/** Max bytes a single import upload may carry (enforced at presign time). */
export const IMAGE_IMPORT_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
/**
 * Object-key contract for import uploads: `users/{userId}/imports/{uuid}.{ext}`.
 * Keys are server-generated and namespaced per user so ownership is provable
 * from the key alone — never trust a client-supplied key.
 */
export function importKeyPrefixForUser(userId) {
    return `users/${userId}/imports/`;
}
/** True when `key` lives under the calling user's import namespace. */
export function isImportKeyOwnedBy(key, userId) {
    return key.startsWith(importKeyPrefixForUser(userId));
}
/**
 * Request a presigned upload URL for an import image. `contentLength` is the
 * exact byte size the client intends to PUT: it is bounded to
 * `IMAGE_IMPORT_MAX_BYTES` here (oversized requests are rejected at the boundary
 * with a 400) and signed into the presigned PUT so the object store rejects a
 * body that does not match the declared length. This is the size half of the
 * ticket's content-type/size constraints — without it an authenticated caller
 * could PUT an arbitrarily large object within the URL's validity window.
 */
export const uploadUrlRequestSchema = z.object({
    contentType: imageImportContentTypeSchema,
    contentLength: z.number().int().positive().max(IMAGE_IMPORT_MAX_BYTES),
});
/**
 * Presigned upload response: PUT the bytes to `uploadUrl`, then start an import
 * with `{ sourceType: 'image' | 'screenshot', imageKey }`.
 */
export const uploadUrlResponseSchema = z.object({
    uploadUrl: z.string().url(),
    imageKey: z.string(),
    expiresIn: z.number().int().positive(),
});
// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------
export const healthCheckResponseSchema = z.object({
    status: z.enum(['ok', 'error']),
    timestamp: z.string(),
    version: z.string(),
    services: z.object({
        database: z.enum(['ok', 'error']),
        redis: z.enum(['ok', 'error']),
        keycloak: z.enum(['ok', 'error']),
    }),
});
// ---------------------------------------------------------------------------
// Admin observability (admin.chefty.app) — all responses below back endpoints
// under `/admin`, gated server-side by the Keycloak `admin` role. They are
// read-only aggregates; no secrets or tokens ever appear in these shapes.
// ---------------------------------------------------------------------------
/** Which AI call a usage row accounts for — mirrors the `ai_usage_kind` db enum. */
export const aiUsageKindSchema = z.enum(['extraction', 'translation']);
/** Tokens + cost rolled up for one provider. */
export const providerUsageSchema = z.object({
    provider: z.string(),
    calls: z.number().int().nonnegative(),
    promptTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    totalTokens: z.number().int().nonnegative(),
    estimatedCostUsd: z.number().nonnegative(),
});
/** One row of the jobs-by-status breakdown. */
export const jobStatusCountSchema = z.object({
    status: aiJobStatusSchema,
    count: z.number().int().nonnegative(),
});
/** GET /admin/stats — top-of-dashboard KPIs. */
export const adminStatsSchema = z.object({
    totalUsers: z.number().int().nonnegative(),
    totalRecipes: z.number().int().nonnegative(),
    totalImportJobs: z.number().int().nonnegative(),
    jobsByStatus: z.array(jobStatusCountSchema),
    totalTokens: z.number().int().nonnegative(),
    totalEstimatedCostUsd: z.number().nonnegative(),
    providerBreakdown: z.array(providerUsageSchema),
});
/** One day of the usage time-series (UTC day buckets). */
export const usageDayPointSchema = z.object({
    date: z.string(), // YYYY-MM-DD (UTC)
    totalTokens: z.number().int().nonnegative(),
    estimatedCostUsd: z.number().nonnegative(),
});
/** GET /admin/usage?days=N — capped window of daily usage. */
export const adminUsageQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(90).default(30),
});
export const adminUsageResponseSchema = z.object({
    series: z.array(usageDayPointSchema),
    providerBreakdown: z.array(providerUsageSchema),
});
/** A user row with the aggregates an operator cares about. */
export const adminUserSummarySchema = z.object({
    id: z.string().uuid(),
    email: z.string().nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    // Derived from a Keycloak `sub` prefixed `anon:` — a device user, not a login.
    anonymous: z.boolean(),
    recipeCount: z.number().int().nonnegative(),
    importJobCount: z.number().int().nonnegative(),
    totalTokens: z.number().int().nonnegative(),
    estimatedCostUsd: z.number().nonnegative(),
    createdAt: z.coerce.date(),
    // Most recent of: their newest import job, newest recipe, or profile update.
    lastActiveAt: z.coerce.date().nullish(),
});
/** GET /admin/users — paginated user directory, optional email search via `q`. */
export const adminUserListQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().min(1).optional(),
});
export const adminUserListResponseSchema = z.object({
    items: z.array(adminUserSummarySchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
});
/** An import job annotated with its owner's email (admin cross-user view). */
export const adminImportJobSchema = importJobSchema.extend({
    userEmail: z.string().nullish(),
});
/**
 * Body for PATCH /admin/users/:id/quota — set a user's PERMANENT extra weekly
 * imports (added on top of LIMITS.FREE_WEEKLY_IMPORTS every rolling window).
 * Capped at 1000 to keep an admin slip from minting an effectively-unlimited
 * free account; set back to 0 to revert.
 */
export const adminQuotaUpdateSchema = z.object({
    extra: z.number().int().min(0).max(1000),
});
/**
 * A user's current free-tier import-quota state — rendered on the admin user
 * page and returned by both quota mutations so the UI can refresh in place.
 * `weeklyLimit` is the effective allowance (free base + extra); `importsUsed`
 * is the reset-aware count in the current window; `importQuotaResetAt` is the
 * last admin reset (ISO) or null.
 */
export const adminQuotaStateSchema = z.object({
    importQuotaExtra: z.number().int().nonnegative(),
    importQuotaResetAt: z.string().datetime().nullable(),
    importsUsed: z.number().int().nonnegative(),
    weeklyLimit: z.number().int().nonnegative(),
});
/** GET /admin/users/:id — one user's profile, content and token totals. */
export const adminUserDetailSchema = z.object({
    user: adminUserSummarySchema,
    quota: adminQuotaStateSchema,
    recipes: z.array(recipeSchema),
    importJobs: z.array(adminImportJobSchema),
    providerBreakdown: z.array(providerUsageSchema),
});
/** A recipe annotated with its owner's email (admin all-recipes view). */
export const adminRecipeSchema = recipeSchema.extend({
    userEmail: z.string().nullish(),
});
/** GET /admin/recipes — all users' recipes, paginated, optional title search. */
export const adminRecipeListQuerySchema = paginationQuerySchema.extend({
    q: z.string().trim().min(1).optional(),
});
export const adminRecipeListResponseSchema = z.object({
    items: z.array(adminRecipeSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
});
/** GET /admin/jobs — all users' import jobs, paginated, optional status filter. */
export const adminJobListQuerySchema = paginationQuerySchema.extend({
    status: aiJobStatusSchema.optional(),
});
export const adminJobListResponseSchema = z.object({
    items: z.array(adminImportJobSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
});
/** Live BullMQ counts for one queue. */
export const queueCountsSchema = z.object({
    name: z.string(),
    active: z.number().int().nonnegative(),
    waiting: z.number().int().nonnegative(),
    delayed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative(),
    paused: z.number().int().nonnegative(),
});
/** GET /admin/jobs/queue — live queue state for every worker queue. */
export const adminQueuesResponseSchema = z.object({
    queues: z.array(queueCountsSchema),
});
// ---------------------------------------------------------------------------
// Campaign links (admin)
// ---------------------------------------------------------------------------
/** A tag value: short, printable, with no separators that would confuse a URL. */
const utmValueSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9._-]+$/, 'Use letters, digits, dots, dashes or underscores');
export const campaignSlugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .refine(isValidCampaignSlug, 'Use 2-64 lowercase letters, digits and single dashes');
export const campaignLinkSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    label: z.string().nullable(),
    targetPath: z.string(),
    utmSource: z.string(),
    utmMedium: z.string(),
    utmCampaign: z.string().nullable(),
    utmContent: z.string().nullable(),
    clicks: z.number().int().nonnegative(),
    lastClickAt: z.string().nullable(),
    createdAt: z.string(),
    /** Ready to paste — built server-side so the admin never assembles it by hand. */
    shortUrl: z.string(),
    /** Where the short link sends people, tags included. */
    targetUrl: z.string(),
});
export const adminCampaignLinkCreateSchema = z.object({
    slug: campaignSlugSchema,
    label: z.string().trim().max(160).optional(),
    /** Site-relative; anything else is forced back under our origin when resolved. */
    targetPath: z.string().trim().max(512).default('/'),
    utmSource: utmValueSchema,
    utmMedium: utmValueSchema,
    utmCampaign: utmValueSchema.optional(),
    utmContent: utmValueSchema.optional(),
});
export const adminCampaignLinkListSchema = z.object({
    links: z.array(campaignLinkSchema),
    /** Total clicks across every link — the "is anything arriving at all" number. */
    totalClicks: z.number().int().nonnegative(),
});
/**
 * GET /admin/conversion — the signup-cohort funnel, from our own Postgres.
 *
 * The cohort is every live account created in the window, anonymous devices
 * included (they are most of the funnel). Each stage counts cohort users who have
 * EVER reached it — so every rate reads as "% of new users", and a stage need not
 * be a subset of the one above it (a user can sign up without seeing the paywall).
 */
export const CONVERSION_STAGES = [
    'new_users', // created in the window
    'activated', // at least one completed import
    'saved_recipe', // at least one recipe in the book
    'paywall_shown', // saw the upgrade screen
    'signed_up', // has a real account, not only an anonymous device identity
    'subscribed', // has ever had a subscription, whatever its status now
];
export const conversionStageSchema = z.enum(CONVERSION_STAGES);
export const adminConversionQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(365).default(30),
});
export const adminConversionResponseSchema = z.object({
    days: z.number().int().positive(),
    stages: z.array(z.object({ stage: conversionStageSchema, users: z.number().int().nonnegative() })),
    /**
     * The monetisation step on its own: of the cohort users who saw the paywall, how
     * many subscribed. Not derivable from `stages`, which are counted independently.
     */
    paywallToPaid: z.object({
        shown: z.number().int().nonnegative(),
        paid: z.number().int().nonnegative(),
    }),
});
// ---------------------------------------------------------------------------
// Product analytics (the G3 measurement gate)
// ---------------------------------------------------------------------------
/**
 * The complete set of events the product may record — a CLOSED list on purpose.
 *
 * Measurement is recorded first-party (our own Postgres) and mirrored to PostHog,
 * and the thing that keeps both honest is this enum: a client cannot invent an
 * event name, so neither the events table nor PostHog can drift into a free-form
 * sink for whatever someone decided to log. Adding a signal is a deliberate change here,
 * reviewed like any other contract change.
 *
 * `app_open` is the retention signal — signup cohorts already come from
 * `users.created_at` and activation from the import history, but "was this user
 * back on day 7" needs a heartbeat that nothing else provides.
 */
export const ANALYTICS_EVENT_NAMES = [
    'app_open',
    'import_started',
    'import_completed',
    'import_failed',
    'recipe_saved',
    'cook_started',
    'cook_completed',
    'paywall_shown',
    'paywall_dismissed',
    // Closes the paywall funnel. Without a completion event `paywall_shown` has no
    // denominator-matching outcome and conversion cannot be computed at all — the
    // measurement gap the G3 gate is about. `purchase_restored` is kept separate so a
    // reinstall is never counted as a new sale.
    'purchase_completed',
    'purchase_restored',
];
export const analyticsEventNameSchema = z.enum(ANALYTICS_EVENT_NAMES);
/**
 * Small, non-identifying qualifiers only (e.g. `{ source: 'url' }`). Bounded in
 * both directions — never let a "flexible" props bag become the place personal
 * data leaks into analytics.
 */
export const analyticsPropsSchema = z
    .record(z.string().max(32), z.union([z.string().max(64), z.number(), z.boolean()]))
    .refine((p) => Object.keys(p).length <= 8, { message: 'At most 8 props per event' });
export const analyticsEventSchema = z.object({
    name: analyticsEventNameSchema,
    /**
     * When it happened on the device. Optional — the server falls back to now, and
     * clamps whatever it is given, because a client clock is not evidence.
     */
    occurredAt: z.string().datetime().optional(),
    props: analyticsPropsSchema.optional(),
});
/** POST /events — a batch, so an offline session can be flushed in one request. */
export const trackEventsRequestSchema = z.object({
    events: z.array(analyticsEventSchema).min(1).max(50),
});
export const trackEventsResponseSchema = z.object({
    accepted: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Egress proxies (admin-managed) — the social media path's outbound network.
// ---------------------------------------------------------------------------
/**
 * Source types whose outbound traffic can be routed through a proxy. YouTube is
 * absent on purpose: the provider fetches that video server-side, so our egress
 * is never involved and a proxy would do nothing but confuse the operator.
 */
export const PROXYABLE_SOURCES = ['tiktok', 'instagram'];
export const proxyableSourceSchema = z.enum(PROXYABLE_SOURCES);
/** Proxy schemes yt-dlp accepts. Anything else is rejected at the boundary. */
export const PROXY_SCHEMES = ['http', 'https', 'socks5', 'socks5h'];
/**
 * A proxy URL carrying credentials, e.g. `socks5://user:pass@host:1080`.
 * Validated here so a malformed entry is rejected in the admin form rather than
 * failing later inside a download the user is waiting on.
 */
export const proxyUrlSchema = z
    .string()
    .trim()
    .min(1)
    .max(1024)
    .refine((value) => {
    try {
        const u = new URL(value);
        return (PROXY_SCHEMES.includes(u.protocol.replace(':', '')) &&
            u.hostname.length > 0);
    }
    catch {
        return false;
    }
}, `Must be a valid proxy URL: ${PROXY_SCHEMES.join(' | ')}://[user:pass@]host:port`);
/**
 * A proxy as the admin panel sees it — **never the password**. The stored URL is
 * split into components on read so credentials cannot leak through the API, a
 * log line, or a screenshot of the admin table.
 */
export const adminProxySchema = z.object({
    id: z.string().uuid(),
    label: z.string(),
    scheme: z.string(),
    host: z.string(),
    port: z.number().int().nullable(),
    username: z.string().nullable(),
    enabled: z.boolean(),
    /**
     * Which sources this proxy serves. Empty means it is never used — the operator
     * controls where paid residential traffic is spent, per source.
     */
    sources: z.array(proxyableSourceSchema),
    /** Non-null while the pool is cooling this proxy down after failures. */
    disabledUntil: z.string().datetime().nullable(),
    lastOkAt: z.string().datetime().nullable(),
    lastFailAt: z.string().datetime().nullable(),
    consecutiveFailures: z.number().int().nonnegative(),
    totalOk: z.number().int().nonnegative(),
    totalFail: z.number().int().nonnegative(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const adminProxyListSchema = z.object({ proxies: z.array(adminProxySchema) });
export const adminProxyCreateSchema = z.object({
    label: z.string().trim().min(1).max(128),
    url: proxyUrlSchema,
    /** Defaults to every proxyable source — the common case is "use it for all". */
    sources: z.array(proxyableSourceSchema).optional(),
    notes: z.string().max(2000).optional(),
});
/** Every field optional — PATCH semantics. Enabling clears any cooldown. */
export const adminProxyUpdateSchema = z
    .object({
    label: z.string().trim().min(1).max(128).optional(),
    url: proxyUrlSchema.optional(),
    enabled: z.boolean().optional(),
    sources: z.array(proxyableSourceSchema).optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, 'Provide at least one field to update');
/** Result of a live probe through one proxy (admin "Test" button). */
export const adminProxyTestResultSchema = z.object({
    ok: z.boolean(),
    latencyMs: z.number().int().nonnegative().nullable(),
    /** The IP the outside world saw — proves traffic actually left via the proxy. */
    egressIp: z.string().nullable(),
    error: z.string().nullable(),
});
//# sourceMappingURL=index.js.map